import { PaymentOptionTypeService } from '../service/index';
import { ProductTypeVerification } from './index';

/**
 * Model for a product to be sold during an event's registration
 */
export class Product {
    public static PRODUCT_TYPE_RAFFLE_ID: string = 'f538c10e-e537-48be-9400-62bc74c426da';
    public static PRODUCT_TYPE_FLOOR_PASS_ID: string = '6e80a0e5-d8bc-4818-8cfb-375ab9858d8d';
    public static PRODUCT_TYPE_APPAREL_ID: string = '39006c37-72e7-463e-871e-d12bb8cfcc23';

    public id: string = null;
    public name: string = null;
    public description: string = null;
    public eventId: string = null;
    public paymentOptionType: string = null;
    public isActive: boolean = false;
    public onlinePrice: number = 0;
    public onsitePrice: number = 0;
    public productTypeId: string = null;
    public productTypeVerification: ProductTypeVerification = new ProductTypeVerification();

    constructor() {
        this.isActive = true;
    }

    public hasOnlinePayment(): boolean {
        return this.paymentOptionType &&
            (
                this.paymentOptionType === PaymentOptionTypeService.ONLINE_ONLY ||
                this.paymentOptionType === PaymentOptionTypeService.ONSITE_ONLINE
            );
    }

    public hasOnsitePayment(): boolean {
        return this.paymentOptionType &&
            (
                this.paymentOptionType === PaymentOptionTypeService.ONSITE_ONLY ||
                this.paymentOptionType === PaymentOptionTypeService.ONSITE_ONLINE
            );
    }

    public getIcon(): string {
        if (this.productTypeId === Product.PRODUCT_TYPE_RAFFLE_ID) {
            return 'fas fa-ticket-alt';
        } if (this.productTypeId === Product.PRODUCT_TYPE_FLOOR_PASS_ID) {
            return 'fas fa-id-badge';
        } if (this.productTypeId === Product.PRODUCT_TYPE_APPAREL_ID) {
            return 'fas fa-shopping-bag';
        }

        return 'fas fa-plus';
    }

    /**
     * Clone the Product object. This is used to prevent object reference issues
     */
    public clone(): Product {
        const clone: Product = new Product();
        clone.id = this.id;
        clone.name = this.name;
        clone.description = this.description;
        clone.eventId = this.eventId;
        clone.paymentOptionType = this.paymentOptionType;
        clone.isActive = this.isActive;
        clone.onlinePrice = this.onlinePrice;
        clone.onsitePrice = this.onsitePrice;
        clone.productTypeId = this.productTypeId;

        if (this.productTypeVerification) {
            clone.productTypeVerification = this.productTypeVerification.clone();
        }

        return clone;
    }

    /**
     * Serialize from a json api format
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'product';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.relationships = {};
        json.data.attributes.name = this.name;
        json.data.attributes.description = this.description;
        json.data.attributes.paymentOptionType = this.paymentOptionType;
        json.data.attributes.isActive = this.isActive;
        this.onlinePrice = this.onlinePrice || 0;
        this.onsitePrice = this.onsitePrice || 0;
        json.data.attributes.onlinePrice = parseFloat(this.onlinePrice.toString());
        json.data.attributes.onsitePrice = parseFloat(this.onsitePrice.toString());

        const productTypeRelationship: any = {};
        productTypeRelationship.data = { type: 'productType', id: this.productTypeId };
        json.data.relationships.productType = productTypeRelationship;

        const productTypeVerificationRelationship: any = {};
        productTypeVerificationRelationship.data = {
            type: 'productTypeVerification',
            id: this.productTypeVerification.id,
        };
        json.data.relationships.productTypeVerification = productTypeVerificationRelationship;

        return JSON.stringify(json);
    }

    /**
     * Deserialize to a json api format
     */
    public deserialize(json: Object, included?: any): Product {
        const data: Object = json['data'] || json;
        const attrs: Object = data['attributes'];
        const relationships: Object[] = data['relationships'] || [];

        this.id = data['id'];
        this.eventId = attrs['eventId'];
        this.name = attrs['name'];
        this.description = attrs['description'];
        this.isActive = attrs['isActive'];
        this.paymentOptionType = attrs['paymentOptionType'];
        this.onlinePrice = attrs['onlinePrice'] ? parseFloat(attrs['onlinePrice']) : 0;
        this.onsitePrice = attrs['onsitePrice'] ? parseFloat(attrs['onsitePrice']) : 0;
        this.productTypeId = attrs['productTypeId'];

        if (attrs['productTypeVerificationId']) {
            this.productTypeVerification = new ProductTypeVerification();
            this.productTypeVerification.id = attrs['productTypeVerificationId'];
        }

        // Fill ProductTypeVerification from relationships if it exists
        const verificationLink: Object = relationships['productTypeVerification'];
        if (verificationLink &&
            included &&
            verificationLink['data'] &&
            verificationLink['data']['id']) {
            const verificationId: string = verificationLink['data']['id'];
            const verification: Object = included.filter(
                (i: Object) => i['type'] === 'productTypeVerification' && i['id'] === verificationId,
            )[0];

            if (verification) {
                this.productTypeVerification = (new ProductTypeVerification()).deserialize(verification);
            }
        }

        return this;
    }
}
